import React, { useEffect, useState } from 'react';
import { Search, Plus, Filter, Download, Users, Eye, Edit2, Trash2, RefreshCw, Loader2, XCircle, Phone, Mail, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { ContactModal } from '../components/ContactModal';
import { contactsApi, CrmContact } from '../services/contactsApi';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { toast } from 'sonner';
export function Contacts() {
  const {
    currentWorkspace,
    currentSubRegion
  } = useWorkspace();
  const [contacts, setContacts] = useState<CrmContact[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeSearch, setActiveSearch] = useState(''); // committed search term for API
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMoreRecords, setHasMoreRecords] = useState(false);
  // Total contact count
  const [totalContacts, setTotalContacts] = useState<number | null>(null);
  const [countLoading, setCountLoading] = useState(true);
  // Modal states
  const [showContactModal, setShowContactModal] = useState(false);
  const [editingContact, setEditingContact] = useState<CrmContact | null>(null);
  const [viewingContact, setViewingContact] = useState<CrmContact | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const perPage = 50;
  const goToPage = async (page: number, search?: string) => {
    const keyword = search ?? activeSearch;
    setLoading(true);
    setError(null);
    try {
      const filters = {
        team: currentWorkspace.name || undefined,
        subRegion: currentSubRegion || undefined
      };
      const hasFilters = !!filters.team || !!filters.subRegion || !!keyword.trim();
      const response = hasFilters ? await contactsApi.search(keyword.trim(), page, perPage, filters) : await contactsApi.list(page, perPage);
      setContacts(response.data || []);
      setCurrentPage(page);
      setHasMoreRecords(response.info?.more_records ?? false);
    } catch (err) {
      console.error('Failed to fetch contacts:', err);
      const msg = err instanceof Error ? err.message : 'Failed to load contacts.';
      setError(msg);
      toast.error('Failed to load contacts', {
        description: msg
      });
    } finally {
      setLoading(false);
    }
  };
  const fetchContacts = () => {
    setActiveSearch('');
    setSearchQuery('');
    goToPage(1, '');
  };
  const handleSearch = () => {
    const keyword = searchQuery.trim();
    setActiveSearch(keyword);
    goToPage(1, keyword);
  };
  const handleClearSearch = () => {
    setSearchQuery('');
    setActiveSearch('');
    goToPage(1, '');
  };
  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };
  useEffect(() => {
    goToPage(1, activeSearch);
    // Fetch total contact count
    const fetchCount = async () => {
      setCountLoading(true);
      try {
        const count = await contactsApi.count({
          team: currentWorkspace.name || undefined,
          subRegion: currentSubRegion || undefined
        });
        setTotalContacts(count);
      } catch (err) {
        console.error('Failed to fetch contact count:', err);
      } finally {
        setCountLoading(false);
      }
    };
    fetchCount();
  }, [currentWorkspace.name, currentSubRegion]);
  const handleAddContact = () => {
    setEditingContact(null);
    setShowContactModal(true);
  };
  const handleEditContact = (contact: CrmContact) => {
    setEditingContact(contact);
    setShowContactModal(true);
  };
  const handleViewContact = async (contact: CrmContact) => {
    setViewingContact(contact);
    try {
      const fullDetails = await contactsApi.get(contact.id);
      if (fullDetails) {
        setViewingContact(fullDetails);
      }
    } catch (err) {
      console.error('Failed to fetch contact details:', err);
      toast.warning('Could not load full details', {
        description: 'Showing partial data.'
      });
    }
  };
  const handleDeleteContact = async (id: string) => {
    setIsDeleting(true);
    try {
      await contactsApi.delete(id);
      setContacts(prev => prev.filter(c => c.id !== id));
      setShowDeleteConfirm(null);
      toast.success('Contact deleted', {
        description: 'The contact has been removed from Zoho CRM.'
      });
    } catch (err) {
      console.error('Failed to delete contact:', err);
      const msg = err instanceof Error ? err.message : 'Unknown error';
      toast.error('Failed to delete contact', {
        description: msg
      });
    } finally {
      setIsDeleting(false);
    }
  };
  const handleModalSuccess = () => {
    setShowContactModal(false);
    fetchContacts();
  };
  // Team and region filtering is now server-side via search criteria
  const filteredContacts = contacts;
  // Pagination bar component — shown above and below the table
  const PaginationBar = () => {
    if (!hasMoreRecords && currentPage === 1) return null;
    return <div className="flex items-center justify-between py-3 px-4 bg-gray-50 border-t border-gray-200 first:border-t-0 first:border-b" data-id="element-3518" data-mp="25ygrjgtq5efr">
        <button onClick={() => goToPage(currentPage - 1)} disabled={currentPage <= 1 || loading} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors" data-id="element-3519" data-mp="21xxxdg4czwae">
          <ChevronLeft className="h-4 w-4" data-id="element-3520" data-mp="1n5mcyo00joc2" />
          Previous
        </button>
        <span className="text-sm text-gray-500" data-id="element-3521" data-mp="1i7kspzqupiz0">
          Page {currentPage}
          {loading && <Loader2 className="inline-block ml-2 h-3.5 w-3.5 animate-spin text-blue-500" data-id="element-3522" data-mp="2yp8l06jenoze" />}
        </span>
        <button onClick={() => goToPage(currentPage + 1)} disabled={!hasMoreRecords || loading} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors" data-id="element-3523" data-mp="1ran07j5eghfr">
          Next
          <ChevronRight className="h-4 w-4" data-id="element-3524" data-mp="259mouqswwwbx" />
        </button>
      </div>;
  };
  return <div className="space-y-6" data-id="element-3525" data-mp="18lq6bgdwzqy2">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-id="element-3526" data-mp="1clh07syqmn6t">
        <div className="min-w-0" data-id="element-3527" data-mp="1hl3gycdrylkd">
          <div className="flex items-center gap-3" data-id="element-3528" data-mp="1hjs9o2l4evoi">
            <h1 className="text-2xl font-semibold text-gray-900" data-id="element-3529" data-mp="1mbjsfqy1bx43">Contacts</h1>
            {countLoading ? <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-gray-100 text-gray-400 text-xs font-medium rounded-full" data-id="element-3530" data-mp="owfo9vqp4tws">
                <Loader2 className="h-3 w-3 animate-spin" data-id="element-3531" data-mp="tty0bv00fp90" />
              </span> : totalContacts !== null ? <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-blue-50 text-blue-700 text-xs font-medium rounded-full border border-blue-100" data-id="element-3532" data-mp="maygjo8mzvp4">
                <Users className="h-3 w-3" data-id="element-3533" data-mp="ccshrfi4n5oh" />
                {totalContacts.toLocaleString()} total
              </span> : null}
          </div>
          <p className="mt-1 text-sm text-gray-500" data-id="element-3534" data-mp="8eb8apb8hj3w">
            Manage your CRM contacts directly from here.
          </p>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0" data-id="element-3535" data-mp="3ur9s4wdw9fci">
          <button onClick={fetchContacts} className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50 whitespace-nowrap" title="Refresh List" data-id="element-3536" data-mp="3upqab1claw8m">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} data-id="element-3537" data-mp="3gqyzbnt0xqop" />
          </button>
          <button className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50 whitespace-nowrap" data-id="element-3538" data-mp="3btgzqo0wcntd">
            <Download className="h-4 w-4 flex-shrink-0" data-id="element-3539" data-mp="18ik57uvarifn" />
            <span data-id="element-3540" data-mp="ujkpjcpridvm">Export</span>
          </button>
          <button onClick={handleAddContact} className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 whitespace-nowrap" data-id="element-3541" data-mp="uirlwkm3o4hp">
            <Plus className="h-4 w-4 flex-shrink-0" data-id="element-3542" data-mp="krofr4ebczbk" />
            <span data-id="element-3543" data-mp="pkzrnjxzg6ac">Add Contact</span>
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-4 bg-white border border-gray-200 rounded-lg p-4" data-id="element-3544" data-mp="awms6czos5b3">
        <div className="relative flex-1 min-w-0 flex gap-2" data-id="element-3545" data-mp="fuocf18umao5">
          <div className="relative flex-1" data-id="element-3546" data-mp="1udjztrw4o0v">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" data-id="element-3547" data-mp="1wqa4u7sy4p0" />
            <input type="text" placeholder="Search by name or email..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} onKeyDown={handleSearchKeyDown} className="pl-9 pr-8 py-2 w-full border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500" data-id="element-3548" data-mp="6vt51lu2iuj9" />
            {searchQuery && <button onClick={handleClearSearch} className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 text-gray-400 hover:text-gray-600 rounded" title="Clear search" data-id="element-3549" data-mp="66stj0p6k4m5">
                <X className="h-3.5 w-3.5" data-id="element-3550" data-mp="fvkf8r3ndsom" />
              </button>}
          </div>
          <button onClick={handleSearch} disabled={loading} className="inline-flex items-center gap-1.5 px-3 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 flex-shrink-0" title="Search" data-id="element-3551" data-mp="ax966il6fm7g">
            <Search className="h-4 w-4" data-id="element-3552" data-mp="38lo293289dzt" />
          </button>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0" data-id="element-3553" data-mp="33upifti15xrt">
          <Filter className="h-4 w-4 text-gray-400 hidden sm:block flex-shrink-0" data-id="element-3554" data-mp="33teiy5vmbc6i" />
          <span className="text-sm text-gray-500" data-id="element-3555" data-mp="2yw4jryiffm5h">
            {contacts.length} records
            {activeSearch && <span className="ml-1 text-blue-600" data-id="element-3556" data-mp="2tuf7ygndr4gc">for "{activeSearch}"</span>}
          </span>
        </div>
      </div>

      {/* Error State */}
      {error && <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm" data-id="element-3557" data-mp="3mlqiylysu1dh">
          {error}
        </div>}

      {/* Contacts Table */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-id="element-3558" data-mp="3cnj4c9rzfven">
        {loading && contacts.length === 0 ? <div className="p-12 text-center" data-id="element-3559" data-mp="268ihctkxqnk7">
            <Loader2 className="mx-auto h-8 w-8 text-blue-500 animate-spin" data-id="element-3560" data-mp="217ugzhatgudb" />
            <p className="mt-2 text-sm text-gray-500" data-id="element-3561" data-mp="219yumqyip7qs">Loading contacts...</p>
          </div> : filteredContacts.length === 0 ? <div className="p-12 text-center" data-id="element-3562" data-mp="loddnns2z7ey">
            <Users className="mx-auto h-12 w-12 text-gray-400" data-id="element-3563" data-mp="lp6bfz92amky" />
            <h3 className="mt-2 text-sm font-semibold text-gray-900" data-id="element-3564" data-mp="qnq1aaqs0iil">
              No contacts found
            </h3>
            <p className="mt-1 text-sm text-gray-500" data-id="element-3565" data-mp="vfhnya6ta43g">
              {searchQuery ? 'Try adjusting your search.' : 'Add your first contact to get started.'}
            </p>
            {!searchQuery && <button onClick={handleAddContact} className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700" data-id="element-3566" data-mp="14h8wrnwner1j">
                <Plus className="h-4 w-4" data-id="element-3567" data-mp="3b6dncp8o3otc" />
                Add First Contact
              </button>}
          </div> : <>
            {/* Pagination — Top */}
            <PaginationBar data-id="element-3568" data-mp="383poz8d5tacm" />

            <div className="overflow-x-auto" data-id="element-3569" data-mp="3850rz5ys6e2o">
              <table className="min-w-full divide-y divide-gray-200" data-id="element-3570" data-mp="342nnslzomo80">
                <thead className="bg-gray-50" data-id="element-3571" data-mp="344gx691ojmjp">
                  <tr data-id="element-3572" data-mp="3i2yffr8tg6u8">
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-3573" data-mp="3i3gip7twblbn">
                      Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-3574" data-mp="3r2bsetw5fdjp">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-3575" data-mp="4o24elw4s7jg">
                      Phone
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-3576" data-mp="elq31mwuxz6w">
                      Tags
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-3577" data-mp="9kskh97xr34v">
                      Mailing Lists
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-3578" data-mp="x7o43fse5eje">
                      Categories
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-3579" data-mp="obf1xtd141tk">
                      Created Time
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-3580" data-mp="ed935kmirbsx">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200" data-id="element-3581" data-mp="3mr7zwohbf17h">
                  {filteredContacts.map(contact => <tr key={contact.id} className="hover:bg-gray-50 group" data-id="element-3582" data-mp="3rozphbuhmb8i">
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-3583" data-mp="yw2f7x8023cj">
                        <div className="flex items-center gap-3" data-id="element-3584" data-mp="yxdlc3ox3n6t">
                          <div className="h-9 w-9 rounded-full bg-gray-100 flex items-center justify-center text-sm font-medium text-gray-600 flex-shrink-0" data-id="element-3585" data-mp="twzn1dd4brlz">
                            {(contact.First_Name?.[0] || contact.Last_Name?.[0] || '?').toUpperCase()}
                          </div>
                          <div className="min-w-0" data-id="element-3586" data-mp="b4op7xxbmjuq">
                            <div className="text-sm font-medium text-gray-900 truncate max-w-[200px]" data-id="element-3587" data-mp="66dgxrmochem">
                              {contact.Full_Name || `${contact.First_Name || ''} ${contact.Last_Name || ''}`}
                            </div>
                            {contact.Title && <div className="text-xs text-gray-500 truncate" data-id="element-3588" data-mp="1vn4mumvmmam4">
                                {contact.Title}
                              </div>}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-3589" data-mp="2cuvyl1s28ogx">
                        {contact.Email ? <div className="flex items-center gap-2 text-sm text-gray-600" data-id="element-3590" data-mp="2hsxejdu6w7o6">
                            <Mail className="h-3 w-3 text-gray-400" data-id="element-3591" data-mp="1k7jj1bkbhgx4" />
                            {contact.Email}
                          </div> : <span className="text-xs text-gray-400" data-id="element-3592" data-mp="1u44qe3jt7444">—</span>}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-3593" data-mp="1z4sd4d108ysj">
                        {contact.Phone || contact.Mobile ? <div className="flex items-center gap-2 text-sm text-gray-600" data-id="element-3594" data-mp="35jjj2v4zj9tf">
                            <Phone className="h-3 w-3 text-gray-400" data-id="element-3595" data-mp="3ah2za0l8two0" />
                            {contact.Phone || contact.Mobile}
                          </div> : <span className="text-xs text-gray-400" data-id="element-3596" data-mp="15h1wlgpua3zt">—</span>}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-3597" data-mp="1jf0aym45u0fb">
                        {contact.Tags ? <div className="flex flex-wrap gap-1 max-w-[200px]" data-id="element-3598" data-mp="rq6mk13ltgun">
                            {contact.Tags.split(',').slice(0, 2).map((tag, i) => <span key={i} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800" data-id="element-3599" data-mp="10iqmd4vmtrh2" data-mpi={i}>
                                  {tag.trim()}
                                </span>)}
                            {contact.Tags.split(',').length > 2 && <span className="text-xs text-gray-500" data-id="element-3600" data-mp="3q0qnv6nttnzs">
                                +{contact.Tags.split(',').length - 2}
                              </span>}
                          </div> : <span className="text-xs text-gray-400" data-id="element-3601" data-mp="3l40aws7bj8il">—</span>}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-3602" data-mp="cnor4wrwqik4">
                        {contact.Mailing_List ? <div className="flex flex-wrap gap-1 max-w-[200px]" data-id="element-3603" data-mp="hk5kc5dyrn42">
                            {contact.Mailing_List.split(',').slice(0, 2).map((list, i) => <span key={i} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700" data-id="element-3604" data-mp="2neor7pw0h00s" data-mpi={i}>
                                  {list.trim()}
                                </span>)}
                            {contact.Mailing_List.split(',').length > 2 && <span className="text-xs text-gray-500" data-id="element-3605" data-mp="1zpukc3c1z463">
                                +{contact.Mailing_List.split(',').length - 2}
                              </span>}
                          </div> : <span className="text-xs text-gray-400" data-id="element-3606" data-mp="2nggi91scf82d">—</span>}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-3607" data-mp="2imlxrw4wyp28">
                        {contact.Categories ? <div className="flex flex-wrap gap-1 max-w-[200px]" data-id="element-3608" data-mp="2el0pyp78l0yw">
                            {contact.Categories.split(',').slice(0, 2).map((cat, i) => <span key={i} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-50 text-purple-700" data-id="element-3609" data-mp="sw5687cy2e4e" data-mpi={i}>
                                  {cat.trim()}
                                </span>)}
                            {contact.Categories.split(',').length > 2 && <span className="text-xs text-gray-500" data-id="element-3610" data-mp="1bs51br17xxs7">
                                +{contact.Categories.split(',').length - 2}
                              </span>}
                          </div> : <span className="text-xs text-gray-400" data-id="element-3611" data-mp="1pkckuh3hrs8y">—</span>}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-3612" data-mp="1uimf8phe6oti">
                        {contact.Created_Time ? new Date(contact.Created_Time).toLocaleDateString() : '—'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right" data-id="element-3613" data-mp="1so6rl022s9q6">
                        <div className="flex items-center justify-end gap-1" data-id="element-3614" data-mp="1xm8g40i9t191">
                          <button onClick={() => handleViewContact(contact)} className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded flex-shrink-0" title="View Details" data-id="element-3615" data-mp="22lba8pwppn28">
                            <Eye className="h-4 w-4" data-id="element-3616" data-mp="2gchjhdpsmz2b" />
                          </button>
                          <button onClick={() => handleEditContact(contact)} className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded flex-shrink-0" title="Edit Contact" data-id="element-3617" data-mp="2lbt3qwpdcmon">
                            <Edit2 className="h-4 w-4" data-id="element-3618" data-mp="jmn9c8ernopg" />
                          </button>
                          <button onClick={() => setShowDeleteConfirm(contact.id)} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded flex-shrink-0" title="Delete Contact" data-id="element-3619" data-mp="entti9c0blm4">
                            <Trash2 className="h-4 w-4" data-id="element-3620" data-mp="12d4wlxgtxs3f" />
                          </button>
                        </div>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>

            {/* Pagination — Bottom */}
            <PaginationBar data-id="element-3621" data-mp="noib1e0m043r" />
          </>}
      </div>

      {/* Contact Modal (Add/Edit) */}
      <ContactModal isOpen={showContactModal} onClose={() => {
      setShowContactModal(false);
      setEditingContact(null);
    }} contact={editingContact} onSuccess={handleModalSuccess} data-id="element-3622" data-mp="1l3mboz8qop6b" />

      {/* View Contact Detail Modal */}
      {viewingContact && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-3623" data-mp="3fkmdwnxva5pu">
          <div className="flex min-h-screen items-center justify-center p-4" data-id="element-3624" data-mp="3fm7646wplckj">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setViewingContact(null)} data-id="element-3625" data-mp="30uou0k6z026g" />
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-lg" data-id="element-3626" data-mp="2rus4yhgo51av">
              <div className="px-6 py-4 border-b border-gray-200" data-id="element-3627" data-mp="2vw5a0pbxt0gq">
                <div className="flex items-center justify-between" data-id="element-3628" data-mp="2vxgdsp5du87u">
                  <h3 className="text-lg font-semibold text-gray-900" data-id="element-3629" data-mp="2hxdwb40zlgt4">
                    Contact Details
                  </h3>
                  <button onClick={() => setViewingContact(null)} className="text-gray-400 hover:text-gray-500" data-id="element-3630" data-mp="2mvp6jfv71tbv">
                    <XCircle className="h-5 w-5" data-id="element-3631" data-mp="l30xgpvvcbo6" />
                  </button>
                </div>
              </div>
              <div className="px-6 py-4 space-y-4" data-id="element-3632" data-mp="25mmk0h6wd7gg">
                <div className="flex items-center gap-4" data-id="element-3633" data-mp="20njt00nr51rh">
                  <div className="h-14 w-14 rounded-full bg-gray-100 flex items-center justify-center text-lg font-medium text-gray-600" data-id="element-3634" data-mp="20kx9r3ri0xtf">
                    {(viewingContact.First_Name?.[0] || viewingContact.Last_Name?.[0] || '?').toUpperCase()}
                  </div>
                  <div data-id="element-3635" data-mp="2jchph56j9bpf">
                    <h4 className="text-lg font-medium text-gray-900" data-id="element-3636" data-mp="2jczscknpao6b">
                      {viewingContact.Full_Name || `${viewingContact.First_Name || ''} ${viewingContact.Last_Name || ''}`}
                    </h4>
                    <p className="text-sm text-gray-500" data-id="element-3637" data-mp="13whjf5gkekf9">
                      {viewingContact.Title}{' '}
                      {viewingContact.Title && viewingContact.Department ? '•' : ''}{' '}
                      {viewingContact.Department}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-100" data-id="element-3638" data-mp="3r3dnz3o6dwid">
                  <div data-id="element-3639" data-mp="3w4t6anyyxox2">
                    <p className="text-xs font-medium text-gray-500 uppercase" data-id="element-3640" data-mp="3w2gsmn08u0pt">
                      Email
                    </p>
                    <p className="mt-1 text-sm text-gray-900 break-all" data-id="element-3641" data-mp="33g5rnb8q5xsh">
                      {viewingContact.Email || '—'}
                    </p>
                  </div>
                  <div data-id="element-3642" data-mp="38ccrqnefwf2i">
                    <p className="text-xs font-medium text-gray-500 uppercase" data-id="element-3643" data-mp="3cfshr0w07h68">
                      Phone
                    </p>
                    <p className="mt-1 text-sm text-gray-900" data-id="element-3644" data-mp="3h7u3ddf2ak8x">
                      {viewingContact.Phone || '—'}
                    </p>
                  </div>
                  <div data-id="element-3645" data-mp="2oq7z6j3rilxh">
                    <p className="text-xs font-medium text-gray-500 uppercase" data-id="element-3646" data-mp="2opoif5pum3l1">
                      Mobile
                    </p>
                    <p className="mt-1 text-sm text-gray-900" data-id="element-3647" data-mp="2h54fe5hm2dil">
                      {viewingContact.Mobile || '—'}
                    </p>
                  </div>
                  <div data-id="element-3648" data-mp="285hdzjovt7pj">
                    <p className="text-xs font-medium text-gray-500 uppercase" data-id="element-3649" data-mp="284pfo13wgvvk">
                      Owner
                    </p>
                    <p className="mt-1 text-sm text-gray-900" data-id="element-3650" data-mp="1y8cmd4c9ok1c">
                      {viewingContact.Owner?.name || '—'}
                    </p>
                  </div>
                  <div data-id="element-3651" data-mp="1tgbervq1nhhn">
                    <p className="text-xs font-medium text-gray-500 uppercase" data-id="element-3652" data-mp="1tftdgkoiyd2v">
                      Tags
                    </p>
                    <p className="mt-1 text-sm text-gray-900" data-id="element-3653" data-mp="1khr2bcb2u41v">
                      {viewingContact.Tags || '—'}
                    </p>
                  </div>
                  <div data-id="element-3654" data-mp="1fjpksvtaucsi">
                    <p className="text-xs font-medium text-gray-500 uppercase" data-id="element-3655" data-mp="1aj1ru4liflvn">
                      Mailing Lists
                    </p>
                    <p className="mt-1 text-sm text-gray-900" data-id="element-3656" data-mp="10ucyarzri668">
                      {viewingContact.Mailing_List || '—'}
                    </p>
                  </div>
                  <div className="col-span-2" data-id="element-3657" data-mp="qxi7ywdn7c31">
                    <p className="text-xs font-medium text-gray-500 uppercase" data-id="element-3658" data-mp="qytaytz9kft3">
                      Categories
                    </p>
                    <p className="mt-1 text-sm text-gray-900" data-id="element-3659" data-mp="1jlxjva1h9ea5">
                      {viewingContact.Categories || '—'}
                    </p>
                  </div>
                </div>

                <div className="pt-4 border-t border-gray-100 text-xs text-gray-400" data-id="element-3660" data-mp="19vue0999c5ue">
                  <p data-id="element-3661" data-mp="19u037nrp9u41">
                    Created:{' '}
                    {viewingContact.Created_Time ? new Date(viewingContact.Created_Time).toLocaleString() : '—'}
                  </p>
                  <p data-id="element-3662" data-mp="27bi1nplwa5hu">
                    Modified:{' '}
                    {viewingContact.Modified_Time ? new Date(viewingContact.Modified_Time).toLocaleString() : '—'}
                  </p>
                  <p className="mt-1 font-mono" data-id="element-3663" data-mp="3n210vqiqoo2d">ID: {viewingContact.id}</p>
                </div>
              </div>
              <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end gap-3" data-id="element-3664" data-mp="5o5cqda65lp2">
                <button onClick={() => setViewingContact(null)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-3665" data-mp="p2hfkjzqtua">
                  Close
                </button>
                <button onClick={() => {
              handleEditContact(viewingContact);
              setViewingContact(null);
            }} className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700" data-id="element-3666" data-mp="3i4sippxlck14">
                  Edit Contact
                </button>
              </div>
            </div>
          </div>
        </div>}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-3667" data-mp="2rrcmtxuzhvyw">
          <div className="flex min-h-screen items-center justify-center p-4" data-id="element-3668" data-mp="2wrihzo0gq0lq">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setShowDeleteConfirm(null)} data-id="element-3669" data-mp="31js6r0pucumf" />
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md p-6" data-id="element-3670" data-mp="35klplfoffj8g">
              <div className="flex items-center gap-4" data-id="element-3671" data-mp="3ai3saobup280">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-red-100 flex items-center justify-center" data-id="element-3672" data-mp="3fihz631pu64g">
                  <Trash2 className="h-6 w-6 text-red-600" data-id="element-3673" data-mp="3kh34uhzqli0a" />
                </div>
                <div data-id="element-3674" data-mp="1l5jxg7u30hsq">
                  <h3 className="text-lg font-medium text-gray-900" data-id="element-3675" data-mp="1l6bqbawao1fb">
                    Delete Contact
                  </h3>
                  <p className="mt-1 text-sm text-gray-500" data-id="element-3676" data-mp="1q67jef3ge8g3">
                    Are you sure you want to delete this contact from Zoho CRM?
                    This action cannot be undone.
                  </p>
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6" data-id="element-3677" data-mp="3ul9sdh4p8fv8">
                <button onClick={() => setShowDeleteConfirm(null)} disabled={isDeleting} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-3678" data-mp="3ujotjfbcqark">
                  Cancel
                </button>
                <button onClick={() => handleDeleteContact(showDeleteConfirm)} disabled={isDeleting} className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 disabled:opacity-50 flex items-center gap-2" data-id="element-3679" data-mp="h6n5ruxeufo1">
                  {isDeleting && <Loader2 className="h-4 w-4 animate-spin" data-id="element-3680" data-mp="2wxj5yc73c4ez" />}
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}